import { Colophon } from '../sections/Colophon.js';
import styles from './LegalPage.module.css';

type LegalLocale = 'fr' | 'en';
type LegalKind = 'privacy' | 'terms' | 'data-deletion';

interface LegalRoute {
  kind: LegalKind;
  locale: LegalLocale;
}

interface LegalSection {
  id: string;
  title: string;
  paragraphs: readonly string[];
  items?: readonly string[];
}

interface LegalDocument {
  title: string;
  eyebrow: string;
  intro: string;
  statusLabel: string;
  status: string;
  effectiveLabel: string;
  effectiveDate: string;
  contactLabel: string;
  contact: string;
  sectionsLabel: string;
  homeLabel: string;
  languageLabel: string;
  alternateHref: string;
  updateBanner?: string;
  referenceDisclaimer?: string;
  sections: readonly LegalSection[];
}

const CONTACT_EMAIL = 'signups@washedafrica.com';

const UPDATE_BANNER_FR =
  'Mise à jour 2026-05-21 — Contenu vérifié contre les flux de données réels (migrations PostgreSQL, code d’API, schéma des litiges, politiques de rétention). Brouillon en attente de validation par conseil juridique avant utilisation en production.';

const UPDATE_BANNER_EN =
  'Update 2026-05-21 — Reference text. This English version is provided for App Store and Play Store review. The authoritative version is the French text at /confidentialite (privacy) and /conditions (terms). In case of any discrepancy, the French text governs.';

const DOCUMENTS: Record<LegalKind, Record<LegalLocale, LegalDocument>> = {
  privacy: {
    fr: {
      title: 'Politique de confidentialité',
      eyebrow: 'Washed · données personnelles',
      intro:
        'Washed organise des visites régulières de blanchisserie à domicile à Lomé. Pour rendre ce service possible, nous traitons certaines données vous concernant — numéro de téléphone, adresse de service, éléments de paiement Mobile Money, signaux techniques de l’application. Ce document précise lesquelles, pourquoi, combien de temps nous les conservons et avec qui nous les partageons. Nous avons fait le choix d’être spécifiques : ce texte correspond à ce que notre code fait réellement.',
      statusLabel: 'Version',
      status: '1.0 — 21 mai 2026',
      effectiveLabel: 'Mise à jour',
      effectiveDate: '21 mai 2026',
      contactLabel: 'Contact',
      contact: CONTACT_EMAIL,
      sectionsLabel: 'Sections de la politique',
      homeLabel: 'Accueil',
      languageLabel: 'English',
      alternateHref: '/privacy',
      updateBanner: UPDATE_BANNER_FR,
      sections: [
        {
          id: 'editeur',
          title: 'Éditeur et responsable',
          paragraphs: [
            'Responsable de traitement : Washed, entité commerciale en cours de constitution à Lomé, Togo.',
            'Adresse postale : à compléter par le fondateur — bureau opérationnel à Lomé.',
            'Référent protection des données : à compléter — le fondateur exerce cette fonction tant qu’un poste dédié n’a pas été créé.',
            'Contact : signups@washedafrica.com.',
          ],
        },
        {
          id: 'preambule',
          title: 'Préambule',
          paragraphs: [
            'Washed met en relation des abonné·e·s avec des prestataires indépendantes — les laveuses — pour une ou plusieurs visites par cycle mensuel, payées par Mobile Money.',
            'Une politique vague protège l’éditeur, pas l’utilisateur. Vous lisez ici un texte qui correspond à ce que notre code fait réellement : flux de données vérifiés contre les migrations PostgreSQL, le code d’API, le schéma des litiges et les politiques de rétention.',
          ],
        },
        {
          id: 'donnees-identite',
          title: 'Données collectées — identité',
          paragraphs: [
            'Lors de la création de votre compte, nous enregistrons les éléments suivants. Le numéro de téléphone est stocké chiffré au repos et indexé par une empreinte (phone_number_lookup_hash) qui permet de retrouver votre compte sans exposer le numéro en clair dans les requêtes internes. Seules les surfaces qui en ont strictement besoin — déclenchement OTP, support, paiement — le déchiffrent.',
          ],
          items: [
            'Prénom et nom.',
            'Numéro de téléphone au format international (par exemple +228 90 12 34 56). C’est l’identifiant principal du compte.',
            'Adresse e-mail (facultative).',
            'Photo de profil (facultative), si vous l’ajoutez depuis l’écran X-26.',
            'Confirmation d’âge adulte (≥ 18 ans). Le service n’est pas accessible aux mineurs.',
          ],
        },
        {
          id: 'donnees-adresse',
          title: 'Adresse de service et localisation',
          paragraphs: [
            'Nous enregistrons votre adresse de service et les éléments qui permettent à la laveuse de vous trouver. Les coordonnées GPS du domicile sont stockées à la fois en clair pour le calcul d’affectation et de routage, et en version chiffrée (gps_latitude_ciphertext, gps_longitude_ciphertext). Lors de la première visite réussie, la laveuse confirme le pin GPS exact de votre portail (verified_address) — ce qui améliore le routage des visites suivantes.',
            'Pendant la fenêtre de visite uniquement, nous collectons des échantillons de position GPS de la laveuse (visit_location_samples) pour alimenter le suivi en route que vous voyez à l’écran X-12. Chaque échantillon porte une date d’expiration et est supprimé automatiquement après 30 minutes. Nous ne collectons jamais votre propre position GPS pendant la visite.',
            'Aux moments de check-in et check-out, la position GPS de la laveuse est enregistrée dans la fiche de la visite — chiffrée. C’est ce qui nous permet de garantir qu’une visite a réellement eu lieu chez vous.',
          ],
          items: [
            'Quartier (par exemple Bè, Adidogomé, Tokoin).',
            'Rue et repère local (landmark) — un point de repère textuel qui aide la laveuse à trouver le portail.',
            'Coordonnées GPS approximatives du domicile, capturées au moment de la saisie de l’adresse.',
            'Adresse vérifiée par check-in laveuse lors de la première visite réussie.',
          ],
        },
        {
          id: 'donnees-paiement',
          title: 'Paiement Mobile Money',
          paragraphs: [
            'Le paiement passe par Mobile Money. Nous ne demandons jamais votre code secret. Vous le saisissez directement sur votre téléphone via la requête USSD ou STK envoyée par votre fournisseur — Washed n’a aucun accès à ce code et ne le reçoit à aucun moment.',
          ],
          items: [
            'Fournisseur Mobile Money : Mixx by Yas ou Flooz.',
            'Numéro Mobile Money au format international.',
            'Identifiants de transaction retournés par notre agrégateur de paiement (PayDunya) et par le fournisseur Mobile Money.',
          ],
        },
        {
          id: 'donnees-visites',
          title: 'Visites et abonnement',
          paragraphs: [
            'Pour chaque visite, nous enregistrons les éléments nécessaires à la coordination du service et à l’instruction d’un éventuel litige. Aujourd’hui, l’application abonnée ne permet pas de joindre vos propres photos à un litige — l’équipe bureau se base sur les photos avant/après prises par la laveuse pour instruire le dossier.',
          ],
          items: [
            'Formule (Solo / Duo / Foyer — S1, S2, D1, D2, F1, F2 selon la grille en vigueur), jour préféré, créneau préféré.',
            'Statuts de visite : scheduled, in_progress, completed, disputed, cancelled, no_show.',
            'Photos de visite avant et après, capturées par la laveuse. Stockage objet S3-compatible, taille maximale 5 Mo, formats image/jpeg, image/png, image/webp.',
            'Notes et commentaires (1–5, commentaire facultatif).',
            'Réclamations : type d’incident (damaged_item, missing_item, worker_no_show, other) et description écrite.',
          ],
        },
        {
          id: 'donnees-techniques',
          title: 'Données techniques et authentification',
          paragraphs: [
            'Quelques signaux techniques nous aident à diagnostiquer les bugs et à sécuriser le compte. Les rapports d’erreur Sentry contiennent le contexte technique du plantage, jamais votre numéro de téléphone, votre adresse, ni vos données de paiement.',
            'À chaque connexion, nous envoyons un code OTP à 6 chiffres par SMS via notre fournisseur KingSMS. Nous stockons une empreinte hachée de ce code pendant sa courte durée de validité — jamais le code en clair. Les sessions authentifiées sont matérialisées par des jetons d’accès et de rafraîchissement chiffrés ; un identifiant d’appareil (device_id) est associé à chaque session pour révoquer à distance un appareil compromis.',
          ],
          items: [
            'Modèle d’appareil et version iOS au lancement de l’application.',
            'Identifiant anonyme d’appareil (anonymous_id) pour les événements de navigation interne.',
            'Événements d’écran consultés (screen_view) avec le nom de l’écran et la durée. Aucune entrée utilisateur n’est enregistrée dans ces événements.',
            'Jetons de notification push (APNS, FCM) si vous acceptez les notifications, chiffrés au repos.',
            'Rapports d’erreur anonymisés Sentry uniquement si une erreur survient.',
          ],
        },
        {
          id: 'utilisation',
          title: 'Pourquoi nous collectons ces données',
          paragraphs: [
            'Chaque donnée a un usage opérationnel précis : connexion par OTP, affectation de la laveuse la plus proche, prélèvement Mobile Money via PayDunya, vérification GPS du service rendu au check-in et check-out, instruction loyale des litiges sur la base des photos avant/après, mesure de la qualité du service.',
            'Préférences facultatives de profil (tranche d’âge, taille du foyer, source de découverte, motivation d’inscription) — strictement facultatives, jamais conditionnantes pour l’accès au service, vidables à tout moment.',
            'Washed ne vend jamais vos données. Aucune donnée n’est utilisée pour de la publicité ciblée, à l’intérieur ou à l’extérieur de l’application.',
          ],
        },
        {
          id: 'partage',
          title: 'Avec qui nous partageons vos données',
          paragraphs: [
            'Nous transmettons uniquement les données strictement nécessaires aux prestataires techniques qui font fonctionner le service. Aucune donnée n’est partagée avec des annonceurs, des courtiers de données ou des plateformes de marketing tiers. Nous communiquons les éléments strictement nécessaires aux autorités togolaises uniquement sur réquisition légale écrite, et nous documentons chaque transmission dans le registre audit_events.',
          ],
          items: [
            'KingSMS (Togo) — envoi du SMS OTP de connexion. Reçoit votre numéro de téléphone et le texte du SMS au moment de l’envoi.',
            'PayDunya (Sénégal, Côte d’Ivoire) — agrégateur de paiement Mobile Money. Reçoit votre numéro Mobile Money, le montant et la référence de transaction.',
            'Mixx by Yas ou Flooz (Togo) — votre fournisseur Mobile Money, qui exécute le débit. Vous validez le débit côté téléphone.',
            'Fly.io (États-Unis, infrastructure déployée en région Paris — cdg) — hébergement de notre API et de notre base PostgreSQL.',
            'Stockage objet S3-compatible (région Paris) — photos avant/après et avatars de profil.',
            'APNS (Apple, États-Unis) et FCM (Google, États-Unis) — acheminement des notifications push.',
            'Sentry — rapports d’erreur anonymisés.',
            'Cloudflare — réseau de diffusion et protection contre les abus. Voit votre adresse IP et le chemin de la requête, jamais le contenu chiffré.',
          ],
        },
        {
          id: 'conservation',
          title: 'Durée de conservation',
          paragraphs: [
            'Notre code applique le tableau de rétention défini dans la migration data_retention_policies (0037). À la suppression de votre compte, nous conservons uniquement les éléments comptables et d’audit requis par la loi togolaise et la convention OHADA pendant la durée nécessaire — typiquement les enregistrements de paiement et les audit_events associés.',
          ],
          items: [
            'Compte, profil, numéro de téléphone — vie du compte plus 24 mois ; anonymisation après suppression sauf obligation comptable.',
            'Adresse, GPS, repères — vie du compte plus 12 mois ; le pin exact est supprimé à la suppression du compte.',
            'Photos de visite (avant/après) — affichage utilisateur 12 mois après la visite ; politique opérationnelle interne 90 jours, ou clôture du litige plus 180 jours si la photo a servi de preuve.',
            'Échantillons GPS en route — supprimés automatiquement 30 minutes après capture.',
            'Notifications (logs de livraison) — 90 jours ; métriques agrégées de taux de livraison seulement au-delà.',
            'Réclamations, litiges, événements de sécurité — 36 mois ; caviardage des éléments personnels en texte libre après clôture lorsque c’est possible.',
            'Événements d’audit (audit_events) — 5 ans, durée imposée par la pratique comptable et conventionnelle OHADA. Table immuable par construction SQL — aucune mise à jour ni suppression possible.',
            'Sessions et jetons d’authentification — jusqu’à expiration ou révocation.',
          ],
        },
        {
          id: 'droits',
          title: 'Vos droits',
          paragraphs: [
            'En tant qu’utilisateur·rice, vous disposez des droits suivants. Nous répondons à toute demande dans un délai maximal de 30 jours calendaires. En cas de désaccord sur notre réponse, vous pouvez saisir l’Instance togolaise de protection des données personnelles (IPDCP).',
            'Comment exercer ces droits : depuis l’application, sur l’écran Vie privée & données (X-27) pour l’export et la suppression. Par e-mail à signups@washedafrica.com pour toute autre demande, en précisant le numéro de téléphone associé à votre compte.',
          ],
          items: [
            'Droit d’accès — consulter les données que nous détenons sur vous depuis X-27 ou par e-mail.',
            'Droit de rectification — modifier nom, adresse, numéro Mobile Money, photo de profil et préférences directement dans l’application.',
            'Droit à l’effacement — demander la suppression de votre compte depuis X-28. La demande est revue par le bureau ; vos données sont anonymisées ou supprimées sous 30 jours, sauf éléments comptables et d’audit que la loi nous oblige à conserver.',
            'Droit à la portabilité — demander un export depuis le bouton Télécharger mes données sur X-27. La demande est enregistrée comme subscriber_privacy_request de type export ; transmise sous environ 30 jours.',
            'Droit d’opposition — refuser certains traitements (notifications push). Le refus de traitements strictement nécessaires au service entraîne l’impossibilité de fournir le service.',
            'Droit à la limitation — suspendre le traitement de certaines données pendant l’instruction d’un litige.',
          ],
        },
        {
          id: 'securite',
          title: 'Sécurité',
          paragraphs: [
            'Aucun système n’est invulnérable. En cas d’incident de sécurité significatif vous concernant, nous vous en informons sans délai injustifié — typiquement sous 72 heures — par notification dans l’application et par e-mail si nous disposons d’une adresse.',
          ],
          items: [
            'Chiffrement en transit — TLS 1.2 ou supérieur sur toute communication application-API, via Cloudflare et Fly.io.',
            'Chiffrement au repos — données sensibles (numéro de téléphone, GPS, jetons push, identifiants d’appareil) chiffrées au niveau application via AES-256-GCM. Rotation de clé outillée et exécutée selon le runbook interne.',
            'Empreintes de recherche — indexation HMAC du numéro de téléphone avec un sel propre à la base. L’empreinte n’est pas réversible.',
            'Cloisonnement par pays (Row-Level Security) — un opérateur Togo ne peut lire que les enregistrements Togo. Règle forcée même pour le propriétaire de la table.',
            'Audit immuable — toute action sensible écrit un enregistrement immuable dans audit_events ; la table n’autorise ni UPDATE ni DELETE par construction SQL.',
            'Accès opérateur restreint — authentification double facteur (TOTP), identifiants gérés via Fly.io secrets. Aucun secret n’est exposé dans le code ni dans les applications client.',
          ],
        },
        {
          id: 'mineurs',
          title: 'Mineurs',
          paragraphs: [
            'Le service Washed est réservé aux personnes majeures (≥ 18 ans). Au moment de l’inscription, vous confirmez avoir l’âge légal. Nous ne créons pas sciemment de compte pour un·e mineur·e. Si nous découvrons qu’un compte a été créé par un·e mineur·e, nous le supprimons et purgeons les données associées dans les meilleurs délais.',
          ],
        },
        {
          id: 'traceurs',
          title: 'Cookies, traceurs publicitaires et SDK tiers',
          paragraphs: [
            'L’application iOS Washed ne contient aucun traceur publicitaire. Les seuls SDK tiers embarqués sont Sentry (exclusivement pour les rapports d’erreur anonymisés), Capacitor et ses plugins officiels (notifications, caméra, géolocalisation, stockage — aucune télémétrie réseau), APNS (Apple) et FCM (Google) uniquement pour les notifications push déclenchées par notre serveur.',
            'Le site marketing washedafrica.com peut utiliser des outils d’analyse de trafic agrégés (Cloudflare Web Analytics), sans cookie publicitaire ni partage avec des plateformes de marketing tiers.',
          ],
        },
        {
          id: 'transferts',
          title: 'Transferts internationaux',
          paragraphs: [
            'Notre infrastructure principale est hébergée à Paris (Fly.io région cdg). Vos données transitent et résident dans un pays disposant d’un cadre de protection des données équivalent au Règlement général sur la protection des données (RGPD).',
            'Quelques fournisseurs sont basés en dehors de l’Union européenne — APNS et FCM aux États-Unis pour les notifications push, PayDunya au Sénégal et en Côte d’Ivoire pour le paiement Mobile Money. Les données transmises à ces fournisseurs sont strictement limitées à ce qui est nécessaire à leur fonction.',
          ],
        },
        {
          id: 'modifications',
          title: 'Modifications de cette politique',
          paragraphs: [
            'Nous pouvons modifier cette politique pour refléter une évolution du service ou une obligation légale nouvelle. En cas de modification substantielle, nous vous prévenons par notification dans l’application au prochain lancement et par e-mail si nous disposons d’une adresse vérifiée.',
            'Vous disposez d’un délai de 30 jours pour examiner les modifications et, le cas échéant, supprimer votre compte si elles ne vous conviennent pas. Une modification mineure (correction d’orthographe, précision rédactionnelle) ne déclenche pas cette notification.',
            'L’historique des versions est conservé dans le dépôt source de l’application.',
          ],
        },
        {
          id: 'loi',
          title: 'Loi applicable et juridiction',
          paragraphs: [
            'La présente politique est régie par la loi togolaise. Pour les aspects de traitement de données personnelles, elle s’inspire des standards RGPD européens et de la jurisprudence émergente des autorités sous-régionales (CDP du Sénégal, CDP du Bénin) en l’attente du déploiement effectif de l’IPDCP au Togo.',
            'Tout litige relatif à cette politique relève des tribunaux de Lomé.',
          ],
        },
      ],
    },
    en: {
      title: 'Privacy policy',
      eyebrow: 'Washed · personal data',
      intro:
        'Washed operates a recurring home laundry service in Lomé. To run this service we process some data about you — phone number, service address, mobile-money payment details, and a few technical signals from the app. This document explains which data, why, how long we keep it, and who we share it with. We chose to be specific: this text matches what our code actually does.',
      statusLabel: 'Version',
      status: '1.0 — May 21, 2026',
      effectiveLabel: 'Updated',
      effectiveDate: 'May 21, 2026',
      contactLabel: 'Contact',
      contact: CONTACT_EMAIL,
      sectionsLabel: 'Policy sections',
      homeLabel: 'Home',
      languageLabel: 'Français',
      alternateHref: '/confidentialite',
      updateBanner: UPDATE_BANNER_EN,
      referenceDisclaimer:
        'This English version is provided for reference. The authoritative version is the French text available at /confidentialite (privacy) and /conditions (terms). In case of any discrepancy, the French text governs.',
      sections: [
        {
          id: 'publisher',
          title: 'Publisher and data controller',
          paragraphs: [
            'Data controller: Washed, a commercial entity being formed in Lomé, Togo.',
            'Postal address: to be completed by the founder — operational bureau in Lomé.',
            'Data protection officer: to be completed — the founder holds this role until a dedicated position is created.',
            'Contact: signups@washedafrica.com.',
          ],
        },
        {
          id: 'preamble',
          title: 'Preamble',
          paragraphs: [
            'Washed connects subscribers with independent service providers — washerwomen (laveuses)¹ — for one or more home visits per monthly cycle, paid by mobile money.',
            'A vague policy protects the publisher, not the user. This text matches what our code actually does: data flows verified against PostgreSQL migrations, API code, the disputes schema, and the retention policies.',
            '¹ "Laveuse" is the Washed product term for the service provider who performs the home laundry visit.',
          ],
        },
        {
          id: 'data-identity',
          title: 'Data collected — identity',
          paragraphs: [
            'When you create your account we record the items listed below. The phone number is stored encrypted at rest and indexed by a fingerprint (phone_number_lookup_hash) that lets us find your account without exposing the number in plaintext in internal queries. Only the surfaces that strictly need it — OTP delivery, support, payment — decrypt it.',
          ],
          items: [
            'First name and last name.',
            'Phone number in international format (for example +228 90 12 34 56). This is the primary account identifier.',
            'Email address (optional).',
            'Profile photo (optional), if you add one from screen X-26.',
            'Adult-age confirmation (≥ 18 years). The service is not available to minors.',
          ],
        },
        {
          id: 'data-address',
          title: 'Service address and location',
          paragraphs: [
            'We record your service address and the cues that let the service provider find you. The home GPS coordinates are stored both in plaintext for routing and assignment, and encrypted (gps_latitude_ciphertext, gps_longitude_ciphertext). On the first successful visit, the service provider confirms the exact GPS pin of your gate (verified_address) — which improves routing for subsequent visits.',
            'During the visit window only, we collect GPS samples from the service provider (visit_location_samples) to power the in-route tracking you see on screen X-12. Each sample carries an expiration timestamp and is deleted automatically after 30 minutes. We never collect your own GPS location during the visit.',
            'At check-in and check-out, the service provider’s GPS location is recorded on the visit record — encrypted. This is how we can verify that a visit actually took place at your home.',
          ],
          items: [
            'Neighborhood (for example Bè, Adidogomé, Tokoin).',
            'Street and landmark — a free-text reference that helps the service provider find the gate.',
            'Approximate home GPS coordinates, captured when you enter the address.',
            'Verified address, set by the service provider at check-in on the first successful visit.',
          ],
        },
        {
          id: 'data-payment',
          title: 'Mobile-money payment',
          paragraphs: [
            'Payment runs through mobile money. We never ask for your mobile-money PIN. You enter it directly on your phone via the USSD or STK request sent by your provider — Washed has no access to that PIN and never receives it.',
          ],
          items: [
            'Mobile-money provider: Mixx by Yas or Flooz.',
            'Mobile-money number in international format.',
            'Transaction identifiers returned by our payment aggregator (PayDunya) and by the mobile-money provider.',
          ],
        },
        {
          id: 'data-visits',
          title: 'Visits and subscription',
          paragraphs: [
            'For each visit we record what is needed to coordinate the service and to resolve any dispute. Today, the subscriber app does not let you attach your own photos to a dispute — the bureau team relies on the before/after photos taken by the service provider to investigate.',
          ],
          items: [
            'Plan (Solo / Duo / Household — S1, S2, D1, D2, F1, F2 per the current price grid), preferred day, preferred time slot.',
            'Visit statuses: scheduled, in_progress, completed, disputed, cancelled, no_show.',
            'Before/after visit photos captured by the service provider. S3-compatible object storage, max 5 MB, formats image/jpeg, image/png, image/webp.',
            'Ratings and comments (1–5, optional comment).',
            'Disputes: incident type (damaged_item, missing_item, worker_no_show, other) and your written description.',
          ],
        },
        {
          id: 'data-technical',
          title: 'Technical data and authentication',
          paragraphs: [
            'A few technical signals help us diagnose bugs and secure the account. Sentry error reports contain the technical context of the crash, never your phone number, address, or payment data.',
            'On every sign-in we send a 6-digit OTP code by SMS via our provider KingSMS. We store a hashed fingerprint of that code for its short validity window — never the code in plaintext. Authenticated sessions are materialized by encrypted access and refresh tokens; a device identifier (device_id) is bound to each session so a compromised device can be revoked remotely.',
          ],
          items: [
            'Device model and iOS version at app launch.',
            'Anonymous device identifier (anonymous_id) for internal navigation events.',
            'Screen-view events with screen name and duration. No user input is recorded in these events.',
            'Push notification tokens (APNS, FCM) if you accept notifications, encrypted at rest.',
            'Anonymized Sentry error reports only when an error occurs.',
          ],
        },
        {
          id: 'use',
          title: 'Why we collect this data',
          paragraphs: [
            'Every piece of data has a precise operational purpose: OTP sign-in, assigning the nearest available service provider, mobile-money charging via PayDunya, GPS verification of service delivery at check-in and check-out, fair resolution of disputes based on before/after photos, and measuring service quality.',
            'Optional profile preferences (age band, household size, discovery source, signup motivation) — strictly optional, never gating access to the service, clearable at any time.',
            'Washed never sells your data. No data is used for targeted advertising, inside or outside the app.',
          ],
        },
        {
          id: 'sharing',
          title: 'Who we share your data with',
          paragraphs: [
            'We share only the data strictly necessary with the technical providers that operate the service. No data is shared with advertisers, data brokers, or third-party marketing platforms. We disclose only the strictly necessary elements to Togolese authorities on written legal request, and document each transmission in the audit_events register.',
          ],
          items: [
            'KingSMS (Togo) — sending the sign-in OTP SMS. Receives your phone number and SMS text at send time.',
            'PayDunya (Senegal, Côte d’Ivoire) — mobile-money payment aggregator. Receives your mobile-money number, amount, and transaction reference.',
            'Mixx by Yas or Flooz (Togo) — your mobile-money provider, which executes the debit. You confirm the debit on your phone.',
            'Fly.io (United States, infrastructure deployed in the Paris region — cdg) — hosts our API and PostgreSQL database.',
            'S3-compatible object storage (Paris region) — before/after photos and profile avatars.',
            'APNS (Apple, United States) and FCM (Google, United States) — push-notification delivery.',
            'Sentry — anonymized error reports.',
            'Cloudflare — content delivery and abuse protection. Sees your IP address and request path, never encrypted content.',
          ],
        },
        {
          id: 'retention',
          title: 'Retention',
          paragraphs: [
            'Our code applies the retention table defined in the data_retention_policies migration (0037). On account deletion we keep only the accounting and audit records required by Togolese law and the OHADA convention for the necessary duration — typically payment records and the associated audit_events.',
          ],
          items: [
            'Account, profile, phone number — account lifetime plus 24 months; anonymization after deletion except where accounting requires retention.',
            'Address, GPS, landmarks — account lifetime plus 12 months; the exact pin is deleted on account deletion.',
            'Visit photos (before/after) — visible to you for 12 months after the visit; internal operational policy 90 days, or dispute closure plus 180 days if the photo served as evidence.',
            'In-route GPS samples — deleted automatically 30 minutes after capture.',
            'Notifications (delivery logs) — 90 days; aggregated delivery-rate metrics only beyond that.',
            'Claims, disputes, safety events — 36 months; redaction of free-text personal items after closure where possible.',
            'Audit events (audit_events) — 5 years, retention imposed by OHADA accounting and contractual practice. Table immutable by SQL construction — no UPDATE or DELETE possible.',
            'Sessions and authentication tokens — until expiry or revocation.',
          ],
        },
        {
          id: 'rights',
          title: 'Your rights',
          paragraphs: [
            'As a user you have the following rights. We respond to any request within a maximum of 30 calendar days. If you disagree with our response, you may file a complaint with the Togolese personal-data protection authority (IPDCP).',
            'How to exercise these rights: from the app, on the Privacy & data screen (X-27) for export and deletion. By email to signups@washedafrica.com for any other request, mentioning the phone number tied to your account.',
          ],
          items: [
            'Right of access — view the data we hold about you from X-27 or by email.',
            'Right to rectification — update your name, address, mobile-money number, profile photo, and preferences directly in the app.',
            'Right to erasure — request account deletion from X-28. The bureau reviews the request; your data is anonymized or deleted within 30 days, except accounting and audit records the law requires us to retain.',
            'Right to portability — request an export from the "Download my data" button on X-27. The request is recorded as subscriber_privacy_request of type export; delivered within roughly 30 days.',
            'Right to object — opt out of certain processing (push notifications). Opting out of processing strictly necessary for the service prevents us from providing the service.',
            'Right to restriction — pause the processing of certain data during dispute investigation.',
          ],
        },
        {
          id: 'security',
          title: 'Security',
          paragraphs: [
            'No system is invulnerable. In case of a significant security incident affecting you, we notify you without undue delay — typically within 72 hours — by in-app notification and by email if we have a verified address on file.',
          ],
          items: [
            'In-transit encryption — TLS 1.2 or higher on all app-API communication, via Cloudflare and Fly.io.',
            'At-rest encryption — sensitive data (phone number, GPS, push tokens, device identifiers) encrypted at the application layer via AES-256-GCM. Key rotation tooled and executed per the internal runbook.',
            'Lookup fingerprints — HMAC indexing of the phone number with a database-specific salt. The fingerprint is not reversible.',
            'Country isolation (Row-Level Security) — a Togo operator can only read Togo records. The rule is enforced even for the table owner.',
            'Immutable audit — every sensitive action writes an immutable record to audit_events; the table allows neither UPDATE nor DELETE by SQL construction.',
            'Restricted operator access — two-factor authentication (TOTP); credentials managed through Fly.io secrets. No secret is exposed in code or in client apps.',
          ],
        },
        {
          id: 'minors',
          title: 'Minors',
          paragraphs: [
            'The Washed service is restricted to adults (≥ 18 years). At signup you confirm legal age. We do not knowingly create accounts for minors. If we discover that an account was created by a minor, we delete it and purge the associated data as quickly as possible.',
          ],
        },
        {
          id: 'trackers',
          title: 'Cookies, advertising trackers, and third-party SDKs',
          paragraphs: [
            'The Washed iOS app contains no advertising trackers. The only third-party SDKs embedded are Sentry (anonymized error reports only), Capacitor and its official plugins (notifications, camera, geolocation, storage — no network telemetry), APNS (Apple) and FCM (Google) only for push notifications triggered by our server.',
            'The marketing site washedafrica.com may use aggregated traffic-analytics tools (Cloudflare Web Analytics), with no advertising cookie and no sharing with third-party marketing platforms.',
          ],
        },
        {
          id: 'transfers',
          title: 'International transfers',
          paragraphs: [
            'Our main infrastructure is hosted in Paris (Fly.io region cdg). Your data transits and resides in a country with a data-protection framework equivalent to the European General Data Protection Regulation (GDPR).',
            'A few providers are based outside the European Union — APNS and FCM in the United States for push notifications, PayDunya in Senegal and Côte d’Ivoire for mobile-money payment. The data transmitted to these providers is strictly limited to what is necessary for their function.',
          ],
        },
        {
          id: 'changes',
          title: 'Changes to this policy',
          paragraphs: [
            'We may modify this policy to reflect a service evolution or a new legal obligation. For a substantive change we notify you by in-app notification on the next launch and by email if we have a verified address on file.',
            'You have 30 days to review the changes and, if appropriate, delete your account if they do not suit you. A minor change (spelling fix, drafting clarification) does not trigger this notification.',
            'Version history is kept in the application source repository.',
          ],
        },
        {
          id: 'law',
          title: 'Governing law and jurisdiction',
          paragraphs: [
            'This policy is governed by Togolese law. For personal-data processing, it draws on European GDPR standards and the emerging case law of sub-regional authorities (CDP of Senegal, CDP of Benin) pending the effective deployment of the IPDCP in Togo.',
            'Any dispute relating to this policy falls under the jurisdiction of the courts of Lomé.',
          ],
        },
      ],
    },
  },
  terms: {
    fr: {
      title: 'Conditions générales',
      eyebrow: 'Washed · conditions d’utilisation',
      intro:
        'Washed est un service d’abonnement à la blanchisserie à domicile. Nous mettons en relation des abonné·e·s à Lomé avec des prestataires indépendantes — les laveuses — pour une ou plusieurs visites par cycle mensuel, payées par Mobile Money. En téléchargeant l’application, en créant un compte ou en souscrivant, vous reconnaissez avoir lu, compris et accepté ces conditions. Nous les avons écrites pour qu’elles soient lisibles.',
      statusLabel: 'Version',
      status: '1.4 — 25 octobre 2026',
      effectiveLabel: 'Mise à jour',
      effectiveDate: '25 octobre 2026',
      contactLabel: 'Contact',
      contact: CONTACT_EMAIL,
      sectionsLabel: 'Sections des conditions',
      homeLabel: 'Accueil',
      languageLabel: 'English',
      alternateHref: '/terms',
      updateBanner: UPDATE_BANNER_FR,
      sections: [
        {
          id: 'editeur',
          title: 'Éditeur',
          paragraphs: [
            'Éditeur : Washed, entité commerciale en cours de constitution à Lomé, Togo.',
            'Contact : signups@washedafrica.com.',
            'Adresse postale : à compléter par le fondateur — bureau opérationnel à Lomé.',
          ],
        },
        {
          id: 'definitions',
          title: 'Définitions',
          paragraphs: ['Dans le présent document, les termes ci-dessous ont le sens suivant.'],
          items: [
            'Washed, nous — l’entité commerciale qui exploite la plateforme.',
            'Abonné·e, vous — la personne physique majeure qui souscrit à une formule Washed pour son foyer.',
            'Laveuse, prestataire — la personne indépendante qui réalise la visite de blanchisserie à votre domicile. Désignée formellement prestataire de service de blanchisserie dans les documents administratifs.',
            'Visite — une session de service rendue à votre domicile par la laveuse, encadrée par un check-in et un check-out géolocalisés.',
            'Formule — l’offre d’abonnement à laquelle vous souscrivez. À la date de cette version : Solo 1× (4 500 XOF/mois), Solo 2× (8 500), Duo 1× (6 000), Duo 2× (11 500), Foyer 1× (9 500, 3 à 5 personnes), Foyer 2× (18 000). La grille de référence est celle affichée dans l’application.',
            'Bureau — l’équipe opérationnelle interne de Washed qui supervise les affectations, les paiements, les litiges et le support.',
            'Application — l’application mobile Washed (iOS d’abord, Android par la suite).',
          ],
        },
        {
          id: 'description',
          title: 'Description du service',
          paragraphs: [
            'Washed propose un service d’abonnement à des visites régulières de blanchisserie à domicile. Ce que nous faisons : nous opérons une plateforme qui permet de souscrire à une formule de visites récurrentes, affecte une laveuse à votre adresse selon le quartier, la capacité disponible et les contraintes opérationnelles, coordonne le calendrier des visites, traite le paiement mensuel par Mobile Money via PayDunya, instruit les litiges éventuels via le bureau, et fournit les outils — application, paiement, suivi, support — qui permettent à la relation entre vous et la laveuse de bien se dérouler.',
            'Ce que nous ne faisons pas : Washed est une plateforme de mise en relation et d’organisation — nous ne sommes ni l’employeur direct des laveuses, ni le fournisseur du service de blanchisserie au sens strict. Les laveuses sont des prestataires indépendantes qui exercent leur activité en autonomie, avec leurs propres outils, savoir-faire et organisation, dans le cadre des règles opérationnelles communes de la plateforme.',
          ],
        },
        {
          id: 'acces',
          title: 'Création de compte et accès',
          paragraphs: [
            'Conditions d’accès : être une personne physique majeure (≥ 18 ans), résider à Lomé dans une zone de service active de Washed (la liste des quartiers servis est visible à l’inscription), disposer d’un numéro de téléphone togolais actif, d’un compte Mobile Money Mixx by Yas ou Flooz, et d’un smartphone compatible (l’application est disponible sur l’App Store iOS dans un premier temps).',
            'Authentification : vous vous connectez par votre numéro de téléphone et un code OTP à six chiffres envoyé par SMS via notre fournisseur KingSMS. Le code n’est valable que pendant une courte durée et ne doit jamais être communiqué à un tiers. Toute connexion à votre compte est réputée provenir de vous tant que vous n’avez pas signalé une compromission. Si vous perdez l’accès à votre numéro de téléphone, contactez signups@washedafrica.com pour engager la procédure de récupération de compte.',
            'Exactitude des informations : vous vous engagez à fournir des informations exactes (nom, adresse, numéro Mobile Money) et à les tenir à jour. Une adresse imprécise ou un numéro Mobile Money erroné peut empêcher la prestation du service et n’ouvre pas droit à remboursement.',
          ],
        },
        {
          id: 'abonnement',
          title: 'Abonnement et paiement',
          paragraphs: [
            'Les formules disponibles et leurs prix sont affichés dans l’application (écrans X-04, X-25) et sur washedafrica.com. Les prix sont libellés en francs CFA (XOF) et s’entendent toutes taxes comprises. Washed se réserve le droit d’ajuster sa grille tarifaire ; toute évolution tarifaire vous est notifiée dans l’application au moins 30 jours avant son application — vous disposez de ce délai pour résilier si la nouvelle grille ne vous convient pas.',
            'Le paiement s’effectue exclusivement par Mobile Money, via les fournisseurs Mixx by Yas ou Flooz, sur le numéro que vous avez enregistré dans l’application. L’exécution du débit passe par notre agrégateur PayDunya. Aucune carte bancaire n’est acceptée à ce stade. Rien n’est prélevé à l’inscription. Le premier prélèvement intervient quand le bureau confirme votre laveuse, avant votre première visite, puis tous les 30 jours selon la formule choisie. Le paiement précède toujours le service : une visite n’a lieu que si le cycle qui la couvre est payé. La date exacte et le montant prélevé sont visibles dans l’écran Historique de facturation (X-26) ainsi que dans les SMS de confirmation de votre fournisseur Mobile Money.',
            'Échec de paiement : si un prélèvement échoue (solde insuffisant, numéro invalide, fournisseur indisponible), nous tentons à nouveau le prélèvement selon une politique de relance encadrée. Votre abonnement passe en statut payment_overdue dans nos systèmes, ce qui est signalé dans l’application. Tant que le prélèvement n’a pas abouti, les visites du cycle concerné n’ont pas lieu ; elles reprennent dès la régularisation. Aucun frais de retard ou pénalité financière ne vous est imputé pour un échec de paiement isolé. Une régularisation manuelle est possible depuis le support.',
            'Reçus : vous recevez un récapitulatif des prélèvements dans l’historique de facturation. Sur demande à signups@washedafrica.com, nous pouvons émettre un reçu individuel pour une transaction donnée.',
          ],
          items: [
            'Solo 1× (S1) — 4 500 XOF par mois, une visite par cycle. Les montants stockés sur les abonnements existants restent inchangés (clause d’antériorité) jusqu’à un changement de formule.',
            'Solo 2× (S2) — 8 500 XOF par mois, deux visites par cycle.',
            'Duo 1× (D1) — 6 000 XOF · Duo 2× (D2) — 11 500 XOF · Foyer 1× (F1) — 9 500 XOF (3 à 5 personnes) · Foyer 2× (F2) — 18 000 XOF.',
            'Personne supplémentaire sur une formule Foyer (6e et 7e personne) — 3 000 XOF par mois en Foyer 1×, 6 000 XOF en Foyer 2×.',
            'Clause d’antériorité (25 octobre 2026) — les foyers dont l’abonnement était payé avant la mise en ligne de cette grille gardent leur prix (Foyer 1× 8 500 XOF, Foyer 2× 16 000 XOF, personne supplémentaire 2 000 / 4 000 XOF, lavage supplémentaire Foyer 8 500 XOF) pour leurs renouvellements jusqu’au 31 janvier 2027 inclus ; le prix de février 2027 leur est indiqué à chaque renouvellement.',
            'Les formules Duo et Foyer couvrent 2 ou 3–5 résidents réguliers à une seule adresse.',
          ],
        },
        {
          id: 'pause-annulation',
          title: 'Pause, modification, annulation',
          paragraphs: [
            'Pause : vous pouvez mettre votre abonnement en pause depuis l’application (écran X-22) — par exemple pendant une absence prolongée. Pendant la pause, aucun prélèvement n’est effectué et aucune visite n’est planifiée. Vous reprenez le service à tout moment via l’écran de reprise (X-22.R).',
            'Modification d’adresse : vous pouvez demander un changement d’adresse depuis l’application (écran X-25). Une nouvelle adresse peut entraîner un changement de laveuse si elle relève d’un autre quartier de service. Le bureau valide la demande et confirme la prise en compte dans un délai raisonnable.',
            'Annulation : vous pouvez résilier votre abonnement à tout moment, sans frais, depuis l’application (écran X-19.R) ou par e-mail à signups@washedafrica.com. La résiliation prend effet à la fin du cycle de facturation en cours. Les visites déjà prélevées restent dues ; les visites non encore prélevées sont annulées sans frais. À la résiliation, vos données sont conservées conformément à notre Politique de confidentialité — l’historique reste consultable depuis votre compte pendant 90 jours, puis purgé selon le calendrier de rétention applicable.',
            'Remplacement de laveuse : vous pouvez demander un changement de laveuse (X-30) si la relation ne fonctionne pas. Le bureau étudie la demande et procède à une nouvelle affectation si la capacité opérationnelle le permet. Aucun motif n’est exigé.',
          ],
        },
        {
          id: 'conduite',
          title: 'Conduite attendue et règles communes',
          paragraphs: [
            'De votre côté (abonné·e) — vous vous engagez à permettre l’accès à votre domicile au moment du créneau prévu, rendre le linge accessible (pas de linge perdu sous des meubles, séparé par couleur si vous le souhaitez), traiter la laveuse avec respect (vouvoiement, politesse de base, environnement sécurisé), signaler un litige honnêtement avec une description claire des faits, ne pas effectuer de paiement direct ni de pourboire imposé en dehors de la plateforme, et respecter la vie privée de la laveuse — pas d’enregistrement audio ou vidéo sans son consentement explicite.',
            'Les paiements hors plateforme exposent les deux parties à des risques et privent la laveuse des garanties opérationnelles de Washed (couverture des litiges, suivi GPS, paiement régularisé).',
            'Du côté de la laveuse — les laveuses s’engagent par ailleurs, par accord prestataire séparé, à arriver au créneau convenu et signaler tout retard à l’avance, effectuer un check-in et un check-out géolocalisés à votre domicile via l’application, capturer une photo avant et une photo après chaque visite pour preuve de service, respecter votre domicile, votre famille et vos biens, et ne pas solliciter de paiement hors plateforme.',
            'Engagement de respect mutuel — tout comportement de harcèlement, de discrimination ou de violence — verbale, physique, à caractère sexuel ou raciste — par l’une ou l’autre des parties peut entraîner la suspension immédiate du compte concerné et, le cas échéant, le signalement aux autorités compétentes.',
          ],
        },
        {
          id: 'litiges',
          title: 'Litiges, qualité et compensation',
          paragraphs: [
            'Signalement : si vous constatez un problème lors d’une visite (visite manquée, retard injustifié, dégât matériel, vêtement manquant ou abîmé, comportement inapproprié), vous pouvez ouvrir une réclamation depuis l’écran Signaler un problème (X-17) en sélectionnant le type d’incident et en décrivant les faits.',
            'Instruction par le bureau : le bureau dispose d’un délai indicatif de 5 jours ouvrés pour instruire votre dossier, en s’appuyant sur les photos avant/après capturées par la laveuse, l’historique GPS des check-in et check-out de la visite, votre description écrite, et le cas échéant un échange complémentaire avec vous et avec la laveuse.',
            'Issues possibles : selon les éléments du dossier, l’issue peut être une visite de rattrapage offerte par Washed, un crédit partiel sur la prochaine facture, un crédit complet équivalent à la visite contestée, ou une compensation matérielle en cas de dégât avéré à un vêtement dans les limites précisées à l’article suivant. Vous êtes informé·e de la décision et de sa justification depuis l’écran de la réclamation.',
            'Recours : si vous contestez l’issue d’un litige, vous pouvez demander un réexamen par e-mail à signups@washedafrica.com. À défaut d’accord amiable, vous conservez vos voies de recours de droit commun.',
          ],
        },
        {
          id: 'responsabilite',
          title: 'Limitation de responsabilité — dommages matériels',
          paragraphs: [
            'Position de la plateforme : Washed est une plateforme de mise en relation et d’organisation. Nous ne fournissons pas directement le service de blanchisserie ; il est exécuté par la laveuse, prestataire indépendante. Nous nous engageons sur la qualité de la plateforme — disponibilité, sécurité des paiements, instruction loyale des litiges. Nous ne nous engageons pas comme responsables directs et systématiques de toute dégradation pouvant survenir lors d’une visite.',
            'Couverture des litiges : pour les dommages matériels avérés à un vêtement (déchirure, décoloration, perte), constatés lors de l’instruction d’un litige et imputables à une faute de la laveuse, Washed peut accorder une compensation. Le plafond par incident reste à confirmer par le conseil juridique et l’éventuel partenaire assurantiel — typiquement entre 5 000 et 25 000 XOF par incident isolé. La compensation est versée sous forme de crédit Washed ou, sur demande motivée, de versement Mobile Money sur votre numéro enregistré.',
            'Exclusions de compensation : vêtements de marque haute couture, vêtements présentant un défaut préexistant, vêtements pour lesquels les instructions d’entretien du fabricant n’ont pas pu être respectées faute d’étiquette lisible, vêtements remis dans un état qui aurait dû conduire à un refus de prise en charge.',
            'Partenariat d’assurance : Washed travaille à la mise en place d’un partenariat avec un assureur local pour couvrir les sinistres au-delà des limites de la compensation interne. Statut à la date de la présente version : en cours de constitution. Cette section sera mise à jour dès qu’une couverture est effective.',
            'Cas exclus en tout état de cause : dégâts dus à un cas de force majeure (catastrophe naturelle, coupure d’eau ou d’électricité majeure, etc.), dégâts dus à un défaut antérieur du vêtement ou à une fragilité non signalée, pertes indirectes (manque à gagner, préjudice moral, etc.), dommages qui résulteraient d’une instruction manifestement non conforme aux usages que vous auriez donnée à la laveuse.',
          ],
        },
        {
          id: 'suspension',
          title: 'Suspension et résiliation par Washed',
          paragraphs: [
            'Nous nous réservons le droit de suspendre ou de résilier votre compte, sans préavis si nécessaire, dans les cas suivants : fraude avérée ou tentative de fraude au paiement, multiplication injustifiée de réclamations contradictoires avec les éléments d’instruction, harcèlement ou menaces ou comportement inapproprié envers une laveuse ou un membre du bureau, demande illégale ou contraire à la vie privée de la laveuse, non-paiement persistant après mise en demeure raisonnable.',
            'La suspension fait l’objet d’une notification claire et motivée. Vous pouvez faire valoir vos observations par e-mail à signups@washedafrica.com. En cas de résiliation pour faute grave, les éventuelles compensations en attente peuvent être annulées.',
          ],
        },
        {
          id: 'propriete',
          title: 'Propriété intellectuelle',
          paragraphs: [
            'La marque Washed, le logo, l’application, le site washedafrica.com, leur identité graphique et leur code source sont la propriété de Washed.',
            'Vous bénéficiez d’une licence d’utilisation personnelle et non transférable de l’application, pour la seule finalité d’utiliser le service.',
            'Les contenus que vous fournissez (commentaires, descriptions de litige, photo de profil) restent votre propriété. Vous accordez à Washed une licence non exclusive d’utilisation à la seule fin d’opérer le service — affichage dans l’application, instruction des litiges, analyse anonymisée de la qualité du service.',
          ],
        },
        {
          id: 'donnees',
          title: 'Données personnelles',
          paragraphs: [
            'Le traitement de vos données personnelles est encadré par la Politique de confidentialité disponible à /confidentialite (FR) ou /privacy (EN) sur washedafrica.com et dans l’application. La Politique de confidentialité fait partie intégrante des présentes CGU.',
          ],
        },
        {
          id: 'modifications',
          title: 'Modifications des CGU',
          paragraphs: [
            'Nous pouvons modifier ces CGU pour refléter une évolution du service ou une obligation légale. En cas de modification substantielle, nous vous prévenons par notification dans l’application au prochain lancement et par e-mail si nous disposons d’une adresse vérifiée.',
            'Vous disposez d’un délai de 30 jours pour examiner les modifications. Si vous continuez à utiliser le service au-delà de ce délai, vous êtes réputé·e avoir accepté la nouvelle version. Si vous refusez les modifications, vous pouvez résilier votre abonnement sans frais. Une modification mineure (correction d’orthographe, précision rédactionnelle, mise à jour de coordonnées) ne déclenche pas cette notification.',
          ],
        },
        {
          id: 'loi',
          title: 'Loi applicable et juridiction',
          paragraphs: [
            'Les présentes CGU sont régies par la loi togolaise.',
            'Pour les aspects commerciaux entrant dans le champ de l’Organisation pour l’Harmonisation en Afrique du Droit des Affaires (OHADA), les Actes uniformes correspondants s’appliquent — notamment l’Acte uniforme relatif au droit commercial général et l’Acte uniforme relatif aux contrats de transport de marchandises par route lorsqu’il y a lieu.',
            'En cas de litige relatif à l’exécution des présentes, les parties cherchent prioritairement une résolution amiable par le biais du support signups@washedafrica.com. À défaut, les tribunaux compétents sont ceux du ressort de Lomé.',
          ],
        },
        {
          id: 'dispositions',
          title: 'Dispositions diverses',
          paragraphs: [
            'Indépendance des clauses — si l’une des clauses des présentes est jugée nulle ou inapplicable par une décision de justice, les autres clauses restent pleinement en vigueur.',
            'Absence de renonciation — le fait pour Washed de ne pas se prévaloir à un moment donné d’une clause des présentes ne vaut pas renonciation à se prévaloir ultérieurement de cette même clause.',
            'Intégralité de l’accord — les présentes CGU, la Politique de confidentialité, et toute communication écrite signée des parties constituent l’intégralité de l’accord entre vous et Washed sur l’objet du service.',
          ],
        },
        {
          id: 'contact',
          title: 'Contact',
          paragraphs: ['Pour toute question, réclamation ou demande relative à ces CGU :'],
          items: [
            'E-mail : signups@washedafrica.com.',
            'Application : support intégré (écrans X-29, X-29.A).',
            'Adresse postale : à compléter par le fondateur — bureau opérationnel à Lomé, Togo.',
          ],
        },
      ],
    },
    en: {
      title: 'Terms of service',
      eyebrow: 'Washed · terms of use',
      intro:
        'Washed is a subscription home-laundry service. We connect subscribers in Lomé with independent service providers — the washerwomen (laveuses) — for one or more home visits per monthly cycle, paid by mobile money. By downloading the app, creating an account, or subscribing, you acknowledge that you have read, understood, and accepted these terms. We wrote them to be readable.',
      statusLabel: 'Version',
      status: '1.4 — October 25, 2026',
      effectiveLabel: 'Updated',
      effectiveDate: 'October 25, 2026',
      contactLabel: 'Contact',
      contact: CONTACT_EMAIL,
      sectionsLabel: 'Terms sections',
      homeLabel: 'Home',
      languageLabel: 'Français',
      alternateHref: '/conditions',
      updateBanner: UPDATE_BANNER_EN,
      referenceDisclaimer:
        'This English version is provided for reference. The authoritative version is the French text available at /confidentialite (privacy) and /conditions (terms). In case of any discrepancy, the French text governs.',
      sections: [
        {
          id: 'publisher',
          title: 'Publisher',
          paragraphs: [
            'Publisher: Washed, a commercial entity being formed in Lomé, Togo.',
            'Contact: signups@washedafrica.com.',
            'Postal address: to be completed by the founder — operational bureau in Lomé.',
          ],
        },
        {
          id: 'definitions',
          title: 'Definitions',
          paragraphs: ['In this document the following terms have the meaning given below.'],
          items: [
            'Washed, we — the commercial entity that operates the platform.',
            'Subscriber, you — the adult individual who subscribes to a Washed plan for their household.',
            'Laveuse, service provider — the independent person who performs the home laundry visit. Formally designated laundry service provider in administrative documents.',
            'Visit — a service session at your home delivered by the service provider, bracketed by geolocated check-in and check-out.',
            'Plan — the subscription you take out. As of this version: Solo 1× (4,500 XOF/month), Solo 2× (8,500), Duo 1× (6,000), Duo 2× (11,500), Household 1× (9,500, 3–5 people), Household 2× (18,000). The reference price grid is the one displayed in the app.',
            'Bureau — the Washed internal operations team that oversees assignments, payments, disputes, and support.',
            'App — the Washed mobile application (iOS first, Android later).',
          ],
        },
        {
          id: 'description',
          title: 'Service description',
          paragraphs: [
            'Washed offers a subscription to recurring home laundry visits. What we do: we operate a platform that lets you subscribe to recurring visits, assign a service provider to your address based on neighborhood, available capacity, and operational constraints, coordinate the visit calendar (preferred day, preferred slot), process the monthly mobile-money payment via PayDunya, investigate disputes through the bureau, and provide the tools — app, payment, tracking, support — that make the relationship between you and the service provider work smoothly.',
            'What we do not do: Washed is a connection and organization platform — we are neither the direct employer of the service providers nor the strict-sense provider of the laundry service. The service providers are independent contractors who operate autonomously, with their own tools, know-how, and organization, within the platform’s common operational rules.',
          ],
        },
        {
          id: 'access',
          title: 'Account creation and access',
          paragraphs: [
            'Access conditions: you must be an adult individual (≥ 18 years), residing in Lomé within an active Washed service zone (the list of served neighborhoods is shown at signup), with an active Togolese phone number, a Mixx by Yas or Flooz mobile-money account, and a compatible smartphone (the app is available on the iOS App Store first).',
            'Authentication: you sign in with your phone number and a six-digit OTP code sent by SMS through our provider KingSMS. The code is valid for only a short window and must never be shared with anyone. Any sign-in to your account is deemed to come from you until you report a compromise. If you lose access to your phone number, contact signups@washedafrica.com to start the account-recovery process.',
            'Accuracy of information: you commit to providing accurate information (name, address, mobile-money number) and to keeping it up to date. An imprecise address or wrong mobile-money number can prevent service delivery and does not entitle you to a refund.',
          ],
        },
        {
          id: 'subscription',
          title: 'Subscription and payment',
          paragraphs: [
            'Available plans and their prices are shown in the app (screens X-04, X-25) and on washedafrica.com. Prices are listed in CFA francs (XOF) and are all-taxes-included. Washed reserves the right to adjust its price grid; any price change is notified in the app at least 30 days before it takes effect — you have that window to cancel if the new grid does not suit you.',
            'Payment is exclusively by mobile money, via Mixx by Yas or Flooz, on the number you registered in the app. The debit is executed by our aggregator PayDunya. No bank cards are accepted at this stage. Nothing is charged at sign-up. The first debit happens when the office confirms your washerwoman, before your first visit, then every 30 days according to your plan. Payment always comes before the service: a visit only takes place once the cycle covering it is paid. The exact date and amount are visible on the Billing history screen (X-26) and in the confirmation SMS from your mobile-money provider.',
            'Payment failure: if a debit fails (insufficient balance, invalid number, provider unavailable), we retry according to a controlled retry policy. Your subscription moves to status payment_overdue in our systems, which is surfaced in the app. Until the debit goes through, visits in the affected cycle do not take place; they resume as soon as the payment is settled. No late fee or financial penalty is charged for an isolated payment failure. Manual resolution is available via support.',
            'Receipts: you receive a summary of debits in the billing history. On request to signups@washedafrica.com, we can issue an individual receipt for a given transaction.',
          ],
          items: [
            'S1 — 3,000 XOF per month, one visit per cycle (price applies to subscriptions taken out on or after June 10, 2026; S1 subscriptions started before that date keep their 2,500 XOF monthly price while the plan stays unchanged — a plan change applies the current grid).',
            'S2 — 4,500 XOF per month, two visits per cycle.',
            'Duo 1× (D1) — 6,000 XOF · Duo 2× (D2) — 11,500 XOF · Household 1× (F1) — 9,500 XOF (3–5 people) · Household 2× (F2) — 18,000 XOF.',
            'Extra person on a Household plan (6th and 7th person) — 3,000 XOF a month on Household 1×, 6,000 XOF on Household 2×.',
            'Grandfathering (October 25, 2026) — households whose subscription was paid before this price list went live keep their price (Household 1× 8,500 XOF, Household 2× 16,000 XOF, extra person 2,000 / 4,000 XOF, Household extra wash 8,500 XOF) for renewals through January 31, 2027; the February 2027 price is shown at each renewal.',
            'Duo and Household plans cover 2 or 3–5 regular residents at one address.',
          ],
        },
        {
          id: 'pause-cancel',
          title: 'Pause, modification, cancellation',
          paragraphs: [
            'Pause: you can pause your subscription from the app (screen X-22) — for example during a long absence. During pause, no debit happens and no visit is scheduled. You resume the service at any time via the resume screen (X-22.R).',
            'Address change: you can request an address change from the app (screen X-25). A new address can lead to a change of service provider if it falls within another service neighborhood. The bureau validates the request and confirms within a reasonable delay.',
            'Cancellation: you can cancel your subscription at any time, free of charge, from the app (screen X-19.R) or by email to signups@washedafrica.com. Cancellation takes effect at the end of the current billing cycle. Visits already debited remain due; visits not yet debited are cancelled without charge. On cancellation, your data is retained per our Privacy Policy — history remains visible from your account for 90 days, then purged according to the applicable retention schedule.',
            'Service provider replacement: you can request to change service provider (X-30) if the relationship does not work. The bureau reviews the request and reassigns if operational capacity allows. No reason is required.',
          ],
        },
        {
          id: 'conduct',
          title: 'Expected conduct and common rules',
          paragraphs: [
            'On your side (subscriber) — you commit to giving access to your home at the scheduled time, making the laundry accessible (no clothes lost under furniture, sorted by color if you choose), treating the service provider with respect (polite forms of address, basic courtesy, safe environment), reporting any dispute honestly with a clear factual description, not making direct payments or imposed tips outside the platform, and respecting the service provider’s privacy — no audio or video recording without her explicit consent.',
            'Off-platform payments expose both parties to risk and deprive the service provider of Washed’s operational protections (dispute coverage, GPS tracking, regularized payment).',
            'On the service provider’s side — service providers also commit, via a separate provider agreement, to arriving at the agreed slot and signaling any delay in advance, completing a geolocated check-in and check-out at your home via the app, capturing a before and an after photo at each visit as proof of service, respecting your home, your family, and your belongings, and not soliciting off-platform payment.',
            'Mutual respect commitment — any behavior of harassment, discrimination, or violence — verbal, physical, sexual, or racist — by either party may lead to the immediate suspension of the relevant account and, where appropriate, reporting to the competent authorities.',
          ],
        },
        {
          id: 'disputes',
          title: 'Disputes, quality, and compensation',
          paragraphs: [
            'Reporting: if you notice a problem during a visit (missed visit, unjustified delay, material damage, missing or damaged garment, inappropriate behavior), you can open a dispute from the Report a problem screen (X-17), selecting the incident type and describing the facts.',
            'Bureau investigation: the bureau has an indicative timeframe of 5 business days to investigate your case, drawing on the before/after photos captured by the service provider, the GPS history of check-in and check-out, your written description, and where useful a follow-up exchange with you and with the service provider.',
            'Possible outcomes: depending on the case file, the outcome may be a makeup visit offered by Washed, a partial credit on the next bill, a full credit equal to the disputed visit, or a material compensation for proven garment damage within the limits set out in the next article. You are informed of the decision and its justification from the dispute screen.',
            'Appeal: if you contest the outcome of a dispute, you can request a review by email to signups@washedafrica.com. Failing an amicable settlement, your ordinary legal remedies remain available.',
          ],
        },
        {
          id: 'liability',
          title: 'Liability limitation — material damage',
          paragraphs: [
            'Platform position: Washed is a connection and organization platform. We do not directly provide the laundry service; it is performed by the service provider, an independent contractor. We commit to the quality of the platform — availability, payment security, fair dispute resolution. We do not commit as the direct and systematic party responsible for any degradation that could occur during a visit.',
            'Dispute coverage: for proven material damage to a garment (tear, discoloration, loss), established during a dispute investigation and attributable to a service-provider fault, Washed may grant a compensation. The per-incident cap remains to be confirmed by legal counsel and the eventual insurance partner — typically between 5,000 and 25,000 XOF per isolated incident. The compensation is paid as Washed credit or, on motivated request, as a mobile-money payment to your registered number.',
            'Exclusions from compensation: haute-couture brand garments, garments with a pre-existing defect, garments for which the manufacturer’s care instructions could not be followed due to an unreadable label, garments handed over in a state that should have led to a refusal of intake.',
            'Insurance partnership: Washed is working on a partnership with a local insurer to cover claims beyond the limits of internal compensation. Status as of this version: under formation. This section will be updated once coverage is effective.',
            'Cases excluded in any event: damage caused by force majeure (natural disaster, major water or power outage, etc.), damage caused by a prior defect of the garment or unsignaled fragility, indirect losses (lost profits, moral damages, etc.), damage resulting from manifestly non-customary instructions you may have given the service provider.',
          ],
        },
        {
          id: 'suspension',
          title: 'Suspension and termination by Washed',
          paragraphs: [
            'We reserve the right to suspend or terminate your account, without notice if necessary, in the following cases: proven payment fraud or fraud attempt, unjustified multiplication of disputes contradicted by investigation elements, harassment or threats or inappropriate behavior toward a service provider or a bureau member, illegal request or request contrary to the service provider’s privacy, persistent non-payment after a reasonable formal notice.',
            'Suspension is subject to a clear and motivated notification. You can submit your observations by email to signups@washedafrica.com. In case of termination for serious fault, any pending compensations may be cancelled.',
          ],
        },
        {
          id: 'ip',
          title: 'Intellectual property',
          paragraphs: [
            'The Washed brand, logo, app, washedafrica.com site, their visual identity, and source code are Washed property.',
            'You benefit from a personal, non-transferable license to use the app for the sole purpose of using the service.',
            'The content you provide (comments, dispute descriptions, profile photo) remains your property. You grant Washed a non-exclusive license to use it for the sole purpose of operating the service — display in the app, dispute investigation, anonymized service-quality analysis.',
          ],
        },
        {
          id: 'data',
          title: 'Personal data',
          paragraphs: [
            'The processing of your personal data is governed by the Privacy Policy available at /confidentialite (FR) or /privacy (EN) on washedafrica.com and in the app. The Privacy Policy is an integral part of these terms.',
          ],
        },
        {
          id: 'changes',
          title: 'Changes to these terms',
          paragraphs: [
            'We may modify these terms to reflect a service evolution or a legal obligation. For a substantive change we notify you by in-app notification on the next launch and by email if we have a verified address on file.',
            'You have 30 days to review the changes. If you continue to use the service after that delay, you are deemed to have accepted the new version. If you refuse the changes, you can cancel your subscription free of charge. A minor change (spelling fix, drafting clarification, contact-info update) does not trigger this notification.',
          ],
        },
        {
          id: 'law',
          title: 'Governing law and jurisdiction',
          paragraphs: [
            'These terms are governed by Togolese law.',
            'For commercial aspects within the scope of the Organisation for the Harmonization of Business Law in Africa (OHADA), the corresponding Uniform Acts apply — notably the Uniform Act on general commercial law and the Uniform Act on contracts for the carriage of goods by road where applicable.',
            'In case of a dispute relating to the performance of these terms, the parties seek an amicable resolution first via support at signups@washedafrica.com. Failing that, the competent courts are those of the Lomé jurisdiction.',
          ],
        },
        {
          id: 'misc',
          title: 'Miscellaneous',
          paragraphs: [
            'Severability — if any clause of these terms is held invalid or unenforceable by a court decision, the other clauses remain fully in force.',
            'No waiver — Washed’s failure at any given moment to rely on a clause of these terms does not constitute a waiver of the right to rely on that same clause later.',
            'Entire agreement — these terms, the Privacy Policy, and any signed written communication between the parties constitute the entire agreement between you and Washed on the subject of the service.',
          ],
        },
        {
          id: 'contact',
          title: 'Contact',
          paragraphs: ['For any question, complaint, or request relating to these terms:'],
          items: [
            'Email: signups@washedafrica.com.',
            'App: integrated support (screens X-29, X-29.A).',
            'Postal address: to be completed by the founder — operational bureau in Lomé, Togo.',
          ],
        },
      ],
    },
  },
  'data-deletion': {
    fr: {
      title: 'Suppression des données',
      eyebrow: 'Washed · choix de confidentialité',
      intro:
        'Cette page explique comment demander l’export, la correction ou la suppression de vos données Washed, et ce qui peut être conservé pour des raisons légales, comptables ou de sécurité.',
      statusLabel: 'Statut',
      status: 'Version bêta opérationnelle',
      effectiveLabel: 'Date',
      effectiveDate: '21 mai 2026',
      contactLabel: 'Contact',
      contact: CONTACT_EMAIL,
      sectionsLabel: 'Sections suppression',
      homeLabel: 'Accueil',
      languageLabel: 'English',
      alternateHref: '/data-deletion',
      sections: [
        {
          id: 'clients',
          title: 'Clients',
          paragraphs: [
            'Dans l’application abonné, ouvrez Profil, puis Vie privée & données. Vous pouvez demander l’export ou lancer la suppression du compte lorsque le parcours est disponible.',
            'Si l’application n’est pas accessible, contactez Washed à signups@washedafrica.com avec le numéro de téléphone lié au compte.',
          ],
        },
        {
          id: 'laveuses',
          title: 'Laveuses',
          paragraphs: [
            'Les laveuses peuvent demander accès, correction ou suppression auprès du bureau Washed ou par le canal de support opérationnel communiqué pendant l’onboarding.',
          ],
        },
        {
          id: 'verification',
          title: 'Vérification',
          paragraphs: [
            'Washed peut vérifier l’identité du demandeur avant d’agir sur les données. Les visites actives, paiements en attente, remboursements, litiges ou incidents de sécurité peuvent devoir être traités avant la suppression complète.',
          ],
        },
        {
          id: 'conserve',
          title: 'Données conservées',
          paragraphs: [
            'Certaines données peuvent être conservées lorsque la loi, la comptabilité, la prévention de fraude, la sécurité des personnes ou un litige l’exige.',
            'Lorsque la suppression complète n’est pas possible, Washed limite l’usage des données et anonymise ce qui peut l’être.',
          ],
        },
        {
          id: 'confirmation',
          title: 'Confirmation',
          paragraphs: [
            'Washed confirme la prise en charge de la demande et communique le résultat par le canal de contact disponible.',
          ],
        },
      ],
    },
    en: {
      title: 'Data deletion',
      eyebrow: 'Washed · privacy choices',
      intro:
        'This page explains how to request export, correction, or deletion of your Washed data, and what may be retained for legal, accounting, or safety reasons.',
      statusLabel: 'Status',
      status: 'Operational beta version',
      effectiveLabel: 'Date',
      effectiveDate: 'May 21, 2026',
      contactLabel: 'Contact',
      contact: CONTACT_EMAIL,
      sectionsLabel: 'Deletion sections',
      homeLabel: 'Home',
      languageLabel: 'Français',
      alternateHref: '/suppression-donnees',
      sections: [
        {
          id: 'customers',
          title: 'Customers',
          paragraphs: [
            'In the subscriber app, open Profile, then Privacy & data. You can request an export or start account deletion when the flow is available.',
            'If the app is unavailable, contact Washed at signups@washedafrica.com using the phone number linked to the account.',
          ],
        },
        {
          id: 'washerwomen',
          title: 'Washerwomen',
          paragraphs: [
            'Washerwomen may request access, correction, or deletion through the Washed bureau or the operational support channel provided during onboarding.',
          ],
        },
        {
          id: 'verification',
          title: 'Verification',
          paragraphs: [
            'Washed may verify the requester’s identity before acting on the data. Active visits, pending payments, refunds, disputes, or safety incidents may need to be handled before full deletion.',
          ],
        },
        {
          id: 'retained',
          title: 'Retained data',
          paragraphs: [
            'Some data may be retained where required for law, accounting, fraud prevention, people safety, or a dispute.',
            'Where full deletion is not possible, Washed limits data use and anonymizes what can be anonymized.',
          ],
        },
        {
          id: 'confirmation',
          title: 'Confirmation',
          paragraphs: [
            'Washed confirms receipt of the request and communicates the result through the available contact channel.',
          ],
        },
      ],
    },
  },
};

export function legalRouteFromPath(pathname: string): LegalRoute | null {
  const path = pathname.replace(/\/+$/u, '') || '/';
  if (path === '/confidentialite') return { kind: 'privacy', locale: 'fr' };
  if (path === '/privacy') return { kind: 'privacy', locale: 'en' };
  if (path === '/conditions') return { kind: 'terms', locale: 'fr' };
  if (path === '/terms') return { kind: 'terms', locale: 'en' };
  if (path === '/suppression-donnees') return { kind: 'data-deletion', locale: 'fr' };
  if (path === '/data-deletion') return { kind: 'data-deletion', locale: 'en' };
  return null;
}

export interface LegalPageProps {
  route: LegalRoute;
}

export function LegalPage({ route }: LegalPageProps) {
  const document = DOCUMENTS[route.kind][route.locale];

  return (
    <div className={styles.page}>
      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <a className={styles.brand} href="/" aria-label="Washed">
            washed<span className={styles.dot}>.</span>
          </a>
          <div className={styles.navLinks}>
            <a href="/">{document.homeLabel}</a>
            <a href={document.alternateHref} className={styles.langToggle}>
              {document.languageLabel}
            </a>
          </div>
        </header>
      </div>
      <main className={styles.main}>
        <p className={`smallcaps ${styles.eyebrow}`}>{document.eyebrow}</p>
        <h1 className={styles.h1}>{document.title}</h1>
        {document.updateBanner === undefined ? null : (
          <p className={styles.updateBanner}>{document.updateBanner}</p>
        )}
        <p className={styles.intro}>{document.intro}</p>
        {document.referenceDisclaimer === undefined ? null : (
          <p className={styles.referenceDisclaimer}>{document.referenceDisclaimer}</p>
        )}
        <dl className={styles.meta}>
          <div className={styles.metaItem}>
            <dt>{document.statusLabel}</dt>
            <dd>{document.status}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt>{document.effectiveLabel}</dt>
            <dd>{document.effectiveDate}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt>{document.contactLabel}</dt>
            <dd>
              <a href={`mailto:${document.contact}`}>{document.contact}</a>
            </dd>
          </div>
        </dl>
        <nav className={styles.quickNav} aria-label={document.sectionsLabel}>
          {document.sections.map((section) => (
            <a key={section.id} href={`#${section.id}`}>
              {section.title}
            </a>
          ))}
        </nav>
        {document.sections.map((section) => (
          <section className={styles.section} id={section.id} key={section.id}>
            <h2>{section.title}</h2>
            <div className={styles.body}>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.items === undefined ? null : (
                <ul>
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        ))}
      </main>
      <Colophon locale={route.locale} />
    </div>
  );
}
